import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { getRequest } from 'utils/api';
import { setCounts } from 'features/gigsSlice';
import { setUnreadCounts } from 'features/chatSlice';
import { getMasquerading } from 'utils/localStorage';
import useIsBuyer from 'hooks/useIsBuyer';
import useGlobalUnreadCount from 'hooks/useGlobalUnreadCount';
import HouseIcon from 'public/icons/house.svg';
import JobsInIcon from 'public/icons/jobs-in.svg';
import MsgIcon from 'public/icons/msg-window-white.svg';
import GigsIcon from 'public/icons/loggedInFooter/gigs.svg';
import ArrowGrowIcon from 'public/icons/arrow-grow-white.svg';
import usePrimaryInterest from 'hooks/usePrimaryInterest';
import useIsSeller from 'hooks/useIsSeller';
import ActiveGigsIcon from 'public/icons/loggedInFooter/active-gigs.svg';
import OpenGigsIcon from 'public/icons/loggedInFooter/open-gigs.svg';
import SearchIcon from 'public/icons/search.svg';
import CrownIcon from 'public/icons/crown/crown-white.svg';
import NavLink from 'components/NavLink';
import Badge from 'components/Badge';
import SwitchPrimaryInterest from 'components/SwitchPrimaryInterest';
import styles from 'styles/components/agency/Sidebar.module.scss';
import useDiscoverBadge from 'hooks/useDiscoverBadge';

const badgeStyle = { top: 7, left: 32, right: 'auto' };

const InfluencerDesktopSidebar = () => {
  const [gigCounts, setGigCounts] = useState({});
  const [masquerading, setMasquerading] = useState(false);
  const router = useRouter();
  const loggedIn = useSelector(state => state.auth.loggedIn);
  const isBuyer = useIsBuyer();
  const isSeller = useIsSeller();
  const primaryInterest = usePrimaryInterest();
  const dispatch = useDispatch();
  const realCounts = useSelector(state => state.gigs.counts);
  const currentUser = useSelector(state => state.auth.currentUser);
  const managingProfile =
    currentUser?.full_management_agency && currentUser?.currently_managing_profile;
  const ordersAndInvitesCount = isBuyer ? gigCounts.orders : gigCounts.invites + gigCounts.orders;

  const totalUnreadCount = useGlobalUnreadCount();
  const { badgeCount, hideBadge } = useDiscoverBadge();

  const getSidebarPosition = () => {
    // If the Links Page there is no header
    if (router.pathname === '/[username]') return 30;
    if (masquerading && managingProfile) return 170;
  };

  useEffect(() => {
    if (loggedIn) {
      getRequest({ endpoint: 'gigs/status_counts' })
        .then(response => {
          setGigCounts(response);
          dispatch(setCounts(response));
        })
        .catch(() => {});

      getRequest({ endpoint: 'chats/unread_counts' })
        .then(response => dispatch(setUnreadCounts(response)))
        .catch(() => {});
    }
  }, [loggedIn, primaryInterest, dispatch]);

  useEffect(() => {
    if (!realCounts) return;

    setGigCounts(realCounts);
  }, [realCounts]);

  useEffect(() => {
    const masquerading = getMasquerading();
    setMasquerading(masquerading);
  }, []);

  return (
    <nav className={styles.sidebar}>
      <ul
        className={managingProfile || masquerading ? styles.masqueradingTop : styles.top}
        style={{ top: getSidebarPosition() }}
      >
        <div className={styles.switchButton}>
          <SwitchPrimaryInterest />
        </div>

        <NavLink href="/dashboard" activeClassName={styles.active}>
          <li className={styles.heading}>
            <HouseIcon className={styles.userIcon} />
            <h3>Dashboard</h3>
          </li>
        </NavLink>

        {isSeller && (
          <NavLink href="/offers" activeClassName={styles.active}>
            <Badge content={ordersAndInvitesCount} badgeStyles={badgeStyle}>
              <li className={styles.heading}>
                <JobsInIcon className={styles.navIcons} width={30} height={30} alt="Jobs In" />

                <h3>Orders & Invites</h3>
              </li>
            </Badge>
          </NavLink>
        )}

        {isBuyer && (
          <NavLink href="/gigs/open" activeClassName={styles.active}>
            <Badge content={gigCounts.open} badgeStyles={badgeStyle}>
              <li className={styles.heading}>
                <OpenGigsIcon width={30} height={30} alt="Suitcase" />

                <h3>Public offers</h3>
              </li>
            </Badge>
          </NavLink>
        )}

        <NavLink href="/gigs/active" activeClassName={styles.active}>
          <Badge content={gigCounts.active} badgeStyles={badgeStyle}>
            <li className={styles.heading}>
              <ActiveGigsIcon width={30} height={30} alt="Suitcase" />

              <h3>Active gigs</h3>
            </li>
          </Badge>
        </NavLink>

        <NavLink href="/gigs/closed" activeClassName={styles.active}>
          <li className={styles.heading}>
            <GigsIcon width={30} height={30} alt="Search" />

            <h3>Archived</h3>
          </li>
        </NavLink>

        {isSeller && (
          <NavLink href={'/leaderboard'} activeClassName={styles.active}>
            <li className={styles.heading}>
              <CrownIcon className={styles.greyColor} width={30} height={30} alt="Crown" />

              <h3>Leaderboard</h3>
            </li>
          </NavLink>
        )}

        {isSeller && (
          <NavLink href={'/earnings'} activeClassName={styles.active}>
            <li className={styles.heading}>
              <ArrowGrowIcon className={styles.greyColor} width={30} height={30} alt="Arrow grow" />

              <h3>Earnings</h3>
            </li>
          </NavLink>
        )}

        <NavLink href={'/discover'} activeClassName={styles.active}>
          <Badge content={isSeller ? badgeCount : 0} maxNumber={45} badgeStyles={badgeStyle}>
            <li className={styles.heading} onClick={hideBadge}>
              <SearchIcon className={styles.greyColor} width={30} height={30} alt="Search" />
              <h3>Discover</h3>
            </li>
          </Badge>
        </NavLink>

        <NavLink href={'/chat'} activeClassName={styles.active}>
          <Badge content={totalUnreadCount} badgeStyles={badgeStyle}>
            <li className={styles.heading}>
              <MsgIcon className={styles.greyColor} width={30} height={30} alt="Search" />

              <h3>Chat</h3>
            </li>
          </Badge>
        </NavLink>
      </ul>
    </nav>
  );
};

export default InfluencerDesktopSidebar;
